// Opening move: SectionBlockNode - wraps lyrics paragraphs with section metadata
// This is a container ElementNode that carries sectionType (verse/chorus/bridge/outro/intro)
// Big play: Enables semantic structure without polluting text content or breaking plain-text export

import {
  ElementNode,
  type LexicalNode,
  type NodeKey,
  type SerializedElementNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type EditorConfig,
  $applyNodeReplacement,
} from 'lexical'

export type SectionType = 'verse' | 'chorus' | 'bridge' | 'outro' | 'intro'

export interface SerializedSectionBlockNode extends SerializedElementNode {
  type: 'section-block'
  version: 1
  sectionType: SectionType
  label?: string
}

/**
 * SectionBlockNode - ElementNode that wraps paragraphs with section metadata
 *
 * Main play: Container node that carries sectionType (verse/chorus/bridge/outro/intro)
 * and optional label for display purposes
 *
 * Features:
 * - Extends ElementNode for proper text extraction (getTextContent() works automatically)
 * - Emits DOM with .section-block wrapper and .section-badge span for visual identification
 * - Uses data-section-type attribute as testing hook
 * - Serialization roundtrip preserves section metadata
 *
 * Big play: This node is intentionally minimal - no decorators, no complex rendering
 * Future epics can enhance styling/interactions while keeping the foundation clean
 */
export class SectionBlockNode extends ElementNode {
  __sectionType: SectionType
  __label?: string

  static getType(): string {
    return 'section-block'
  }

  static clone(node: SectionBlockNode): SectionBlockNode {
    return new SectionBlockNode(node.__sectionType, node.__label, node.__key)
  }

  constructor(sectionType: SectionType, label?: string, key?: NodeKey) {
    super(key)
    this.__sectionType = sectionType
    this.__label = label
  }

  // Main play: DOM creation with section badge for visual identification
  createDOM(_config: EditorConfig): HTMLElement {
    const dom = document.createElement('div')
    dom.classList.add('section-block')
    dom.dataset.sectionType = this.__sectionType

    // Time to tackle the tricky bit: Create badge element for section type label
    const badge = this._getOrCreateBadge(dom)
    badge.textContent = this.__label || this.__sectionType

    // Big play: Badge goes FIRST, then we let Lexical render children after it
    // Enforce badge as firstChild to maintain ordering invariant
    if (dom.firstChild !== badge) {
      dom.insertBefore(badge, dom.firstChild)
    }

    return dom
  }

  // Main play: Update DOM when section type changes
  updateDOM(prevNode: SectionBlockNode, dom: HTMLElement): boolean {
    // Time to tackle the tricky bit: Only update if sectionType or label changed
    if (
      prevNode.__sectionType !== this.__sectionType ||
      prevNode.__label !== this.__label
    ) {
      dom.dataset.sectionType = this.__sectionType

      // Big play: Ensure badge is always first child (critical for undo/redo stability)
      const badge = this._getOrCreateBadge(dom)
      badge.textContent = this.__label || this.__sectionType

      // Victory lap: Enforce badge ordering even during DOM reconciliation
      if (dom.firstChild !== badge) {
        dom.insertBefore(badge, dom.firstChild)
      }

      return true // Signal Lexical to reconcile children
    }

    // Time to tackle the tricky bit: Even if content didn't change, enforce badge order
    // This guards against undo/redo operations that may have reconciled DOM out of order
    const badge = this._getOrCreateBadge(dom)
    if (dom.firstChild !== badge) {
      dom.insertBefore(badge, dom.firstChild)
      return true // Trigger reconciliation to fix ordering
    }

    return false // No DOM updates needed
  }

  // Helper: Get existing badge or create new one (reuse to avoid duplication)
  private _getOrCreateBadge(dom: HTMLElement): HTMLSpanElement {
    let badge = dom.querySelector('.section-badge') as HTMLSpanElement | null

    if (!badge) {
      badge = document.createElement('span')
      badge.classList.add('section-badge')
      // Big play: Make badge non-editable to keep cursor out
      badge.contentEditable = 'false'
      // Victory lap: Remove from tab order so keyboard nav skips it
      badge.tabIndex = -1
      dom.appendChild(badge)
    }

    return badge
  }

  // Big play: Serialize section metadata for JSON export
  exportJSON(): SerializedSectionBlockNode {
    return {
      ...super.exportJSON(),
      type: 'section-block',
      version: 1,
      sectionType: this.__sectionType,
      label: this.__label,
    }
  }

  // Main play: Deserialize from JSON import
  static importJSON(serializedNode: SerializedSectionBlockNode): SectionBlockNode {
    const node = $createSectionBlockNode(
      serializedNode.sectionType,
      serializedNode.label
    )
    return node
  }

  // Accessor methods following Lexical best practices
  getSectionType(): SectionType {
    return this.getLatest().__sectionType
  }

  setSectionType(sectionType: SectionType): this {
    const self = this.getWritable()
    self.__sectionType = sectionType
    return self
  }

  getLabel(): string | undefined {
    return this.getLatest().__label
  }

  setLabel(label: string | undefined): this {
    const self = this.getWritable()
    self.__label = label
    return self
  }

  // Time to tackle the tricky bit: Optional DOM conversion for paste handling
  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (domNode.classList.contains('section-block')) {
          return {
            conversion: convertSectionBlockElement,
            priority: 1,
          }
        }
        return null
      },
    }
  }
}

// Helper function to convert DOM back to SectionBlockNode
function convertSectionBlockElement(element: HTMLElement): DOMConversionOutput | null {
  const sectionType = element.dataset.sectionType as SectionType
  if (!sectionType) return null

  const node = $createSectionBlockNode(sectionType)
  return { node }
}

// Victory lap: Factory function with node replacement for proper Lexical integration
export function $createSectionBlockNode(
  sectionType: SectionType,
  label?: string
): SectionBlockNode {
  return $applyNodeReplacement(new SectionBlockNode(sectionType, label))
}

// Type guard for SectionBlockNode
export function $isSectionBlockNode(
  node: LexicalNode | null | undefined
): node is SectionBlockNode {
  return node instanceof SectionBlockNode
}

// Victory lap: Clean, minimal node that carries section metadata without breaking text extraction
// Text content flows through naturally via ElementNode's default getTextContent() implementation
